import type { AgentEditorTab } from '../agent/agent.model';
import type { WorkflowEditorTab } from '../workflow/workflow.model';

export const EDITOR_TAB_QUERY_KEY = 'tab';

export function parseWorkflowEditorTabFromUrl(raw: string): WorkflowEditorTab {
  if (raw === 'graph' || raw === 'runs') {
    return raw;
  }
  throw new Error(`Invalid workflow editor tab: ${raw}`);
}

export function serializeWorkflowEditorTabToUrl(
  tab: WorkflowEditorTab
): string {
  // Runs panel is open by default; only persist collapsed (`graph`) in the URL.
  return tab === 'runs' ? '' : tab;
}

export type WorkflowEditorTabSyncResult = {
  /** Tab to apply, or `null` to leave the current tab untouched. */
  tab: WorkflowEditorTab | null;
  /** The `tab` param value to remember for the next sync. */
  lastRaw: string | null;
};

/**
 * Resolve what the editor tab should become when the URL changes, given the
 * `tab` query value now (`raw`), the value at the previous sync (`lastRaw`, or
 * `undefined` before the first sync), and the tab the editor started on.
 *
 * The rules that make run-id writes safe:
 * - **Unchanged `tab` param → leave the tab alone.** This runs on every
 *   searchParams change, so writing an unrelated param (the open run id) must not
 *   re-resolve the tab — otherwise the runs panel would collapse whenever `?run=`
 *   is written or cleared.
 * - **Absent `tab` param → "runs".** `serializeWorkflowEditorTabToUrl` maps
 *   `runs` to an empty value, so no param canonically means the runs panel is
 *   open — except on the very first sync, which honors `initialTab` so a
 *   graph-only artifact starts on the canvas.
 * - **Present but unparseable → leave the tab alone** (mirrors the tolerant
 *   `UrlParamWatchable.syncFromUrl`).
 */
export function resolveWorkflowEditorTabSync(input: {
  raw: string | null;
  lastRaw: string | null | undefined;
  initialTab: WorkflowEditorTab;
}): WorkflowEditorTabSyncResult {
  const { raw, lastRaw, initialTab } = input;
  const firstSync = lastRaw === undefined;
  if (!firstSync && raw === lastRaw) {
    return { tab: null, lastRaw: lastRaw ?? null };
  }
  if (raw === null || raw === '') {
    return { tab: firstSync ? initialTab : 'runs', lastRaw: raw };
  }
  try {
    return { tab: parseWorkflowEditorTabFromUrl(raw), lastRaw: raw };
  } catch {
    return { tab: null, lastRaw: raw };
  }
}

export function parseAgentEditorTabFromUrl(raw: string): AgentEditorTab {
  if (raw === 'editor' || raw === 'chats') {
    return raw;
  }
  throw new Error(`Invalid agent editor tab: ${raw}`);
}

export function serializeAgentEditorTabToUrl(tab: AgentEditorTab): string {
  return tab === 'editor' ? '' : tab;
}
